import countries from './countries.json';

export interface ShowWhenCondition {
  field: string;
  equals?: string | number | boolean;
  in?: (string | number | boolean)[];
}

export interface FormFieldConfig {
  name: string;
  type: 'text' | 'email' | 'number' | 'password' | 'textarea' | 'select' | 'tags';
  label: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  rows?: number;
  options?: { value: string | number; label: string; countryCode?: string; dialCode?: string; currencyCode?: string; totalCities?: number; totalDistricts?: number }[];
  className?: string;
  showWhen?: ShowWhenCondition;
  validation?: {
    pattern?: string;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    errorMessage?: string;
    validate?: (value: any) => boolean | string;
  };
}

export interface FormSchema {
  formId: string;
  createTitle?: string;
  updateTitle?: string;
  description?: string;
  fields: FormFieldConfig[];
  submitButton?: {
    create: string;
    update: string;
  };
}

export const useLocationSchemas = (countriesList: any[] = [], citiesList: any[] = []) => {
  const schemas: Record<string, FormSchema> = {
    locationAdd: {
      formId: 'location-add',
      createTitle: 'Create New Country',
      fields: [
        {
          name: 'countryName',
          type: 'select',
          label: 'Country',
          required: true,
          className: 'md:col-span-3',
          options: countries.map((c) => ({
            value: c.name,
            label: c.name,
            countryCode: c.countryCode,
            currencyCode: c.currencyCode,
            dialCode: c.dialCode,
            totalCities: c.totalCities,
            totalDistricts: c.totalDistricts,
          })),
        },
        {
          name: 'countryCode',
          type: 'text',
          label: 'Country Code',
          required: true,
          disabled: true,
          className: 'md:col-span-1',
        },
        {
          name: 'dialCode',
          type: 'text',
          label: 'Dial Code',
          required: true,
          disabled: true,
          className: 'md:col-span-1',
        },
        {
          name: 'currencyCode',
          type: 'text',
          label: 'Currency Code',
          required: true,
          disabled: true,
          className: 'md:col-span-1',
        },
        {
          name: 'totalCities',
          type: 'number',
          label: 'Cities',
          className: 'md:col-span-1',
        },
        {
          name: 'totalDistricts',
          type: 'number',
          label: 'Districts',
          className: 'md:col-span-1',
        },
      ],
      submitButton: {
        create: 'Add Country',
        update: 'Update Country',
      },
    },

    cityAdd: {
      formId: 'city-add',
      createTitle: 'Create New City',
      fields: [
        {
          name: 'countryId',
          type: 'select',
          label: 'Country',
          required: true,
          disabled: true,
          className: 'md:col-span-3',
          options: countriesList?.map((c: any) => ({
            value: c.id,
            label: c.name,
          })) || [],
        },
        {
          name: 'name',
          type: 'text',
          label: 'City Name',
          required: true,
          disabled: false,
          className: 'md:col-span-1',
        },
        {
          name: 'arabicName',
          type: 'text',
          label: 'City Arabic Name',
          required: true,
          disabled: false,
          className: 'md:col-span-1',
        },
        {
          name: 'cityCode',
          type: 'text',
          label: 'City Code',
          required: false,
          disabled: false,
          className: 'md:col-span-1',
        },
        {
          name: 'aliasEnglish',
          type: 'tags',
          label: 'Alias English',
          required: false,
          disabled: false,
          className: 'md:col-span-1',
        },
        {
          name: 'aliasArabic',
          type: 'tags',
          label: 'Alias Arabic',
          required: false,
          disabled: false,
          className: 'md:col-span-1',
        },
      ],
      submitButton: {
        create: 'Add City',
        update: 'Update City',
      },
    },

    districtAdd: {
      formId: 'district-add',
      createTitle: 'Create New District',
      fields: [
        {
          name: 'countryId',
          type: 'select',
          label: 'Country',
          required: true,
          disabled: true,
          className: 'md:col-span-3',
          options: countriesList?.map((c: any) => ({
            value: c.id,
            label: c.name,
          })) || [],
        },
        {
          name: 'cityId',
          type: 'select',
          label: 'City',
          required: true,
          disabled: true,
          className: 'md:col-span-3',
          options: citiesList?.map((c: any) => ({
            value: c.id,
            label: c.name,
          })) || [],
        },
        {
          name: 'districtName',
          type: 'text',
          label: 'District Name',
          required: true,
          disabled: false,
          className: 'md:col-span-2',
        },
        {
          name: 'districtArabic',
          type: 'text',
          label: 'District Arabic Name',
          required: true,
          disabled: false,
          className: 'md:col-span-2',
        },
        {
          name: 'aliasEnglish',
          type: 'tags',
          label: 'Alias English',
          required: false,
          disabled: false,
          className: 'md:col-span-2',
        },
        {
          name: 'aliasArabic',
          type: 'tags',
          label: 'Alias Arabic',
          required: false,
          disabled: false,
          className: 'md:col-span-2',
        },
      ],
      submitButton: {
        create: 'Add District',
        update: 'Update District',
      },
    },
  };

  return { schemas };
};
